'use client';

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Phone from '@icons/Phone.svg';

import { Label } from '@components/Common';
import { Input } from '@components/Input';

import { CodeContainer, Container, CountryCodeText, PhoneInputWrapper } from './styles';

export interface PhoneInputProps {
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasError?: boolean;
  required?: boolean;
  disabled?: boolean;
  renderKey?: number;
  variant?: 'default' | 'compact';
}

/**
 * PhoneInput - A reusable phone input component with +31 country code
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  id = 'phoneNumber',
  name = 'phone',
  label,
  placeholder,
  value,
  onChange,
  hasError = false,
  required = false,
  disabled = false,
  renderKey,
  variant = 'default',
}) => {
  const { t } = useTranslation('common');

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e);
    },
    [onChange],
  );

  const displayLabel = label || t('phoneNumber');
  const displayPlaceholder = placeholder || t('enterPhone');

  return (
    <PhoneInputWrapper>
      <Label>{displayLabel}</Label>
      <Container>
        <CodeContainer>
          <Phone />
          <CountryCodeText>+31</CountryCodeText>
        </CodeContainer>
        <Input
          key={renderKey ? `phone-${renderKey}` : undefined}
          type="text"
          id={id}
          placeholder={displayPlaceholder}
          name={name}
          required={required}
          value={value}
          hasError={hasError}
          hasIcon={false}
          disabled={disabled}
          onChange={handlePhoneChange}
        />
      </Container>
    </PhoneInputWrapper>
  );
};
